import * as React from 'react';
import { Column, Row, useTable } from 'react-table';
import { Alert } from 'antd';
import styled from 'styled-components';

import { Table, TableBody, TableCell, TableHead, TableRow } from 'atoms';
import { InfiniteScrollContainer, LoadingData, LoadingTable } from 'molecules';

export type ZoomColumn<D extends object> = Column<D> & {
  /** Fixed column width forwarded to the `atoms/TableCell`. */
  columnWidth?: string;
};

interface IZoomTableProps<D extends object> {
  columns: Array<ZoomColumn<D>>;
  data: D[];
  noDataText: string;
  loading?: boolean;
  error?: string;
  hasMore?: boolean;
  loadMore?: () => void;
}

const noop = () => undefined;

/**
 * Generic react-table v7 list for the Zoom section — wraps `atoms/Table*` and
 * `InfiniteScrollContainer`. Each screen supplies its own column defs
 * (`Cell` renderers welcome).
 */
export function ZoomTable<D extends object>({
  columns,
  data,
  noDataText,
  loading = false,
  error,
  hasMore = false,
  loadMore = noop,
}: IZoomTableProps<D>) {
  const {
    getTableProps,
    headers,
    getTableBodyProps,
    rows,
    prepareRow,
  } = useTable<D>({ columns, data });

  return (
    <Wrapper>
      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 12 }}
        />
      ) : null}
      <InfiniteScrollContainer
        loadMore={loadMore}
        hasMore={hasMore}
        dataLength={rows.length}
        loading={loading}
      >
        <Table {...getTableProps()} flexible>
          <TableHead>
            <TableRow type="row">
              {headers.map((column) => {
                const { key, ...rest } = column.getHeaderProps({
                  type: 'headerCell',
                  width: (column as { columnWidth?: string }).columnWidth,
                } as never);
                return (
                  <TableCell key={key} {...rest}>
                    {column.render('Header')}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {!rows.length && !loading && (
              <TableRow key="noData">
                <TableCell type="cell" width="100%" align="center">
                  {noDataText}
                </TableCell>
              </TableRow>
            )}
            {!rows.length && loading && (
              <TableRow key="loading">
                <TableCell width="100%" align="center">
                  <LoadingTable />
                </TableCell>
              </TableRow>
            )}
            {rows.map((row: Row<D>) => {
              prepareRow(row);
              const { key: rowKey, ...rowRest } = row.getRowProps();
              return (
                <TableRow key={rowKey} type="row" {...rowRest}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellRest } = cell.getCellProps({
                      width: (cell.column as { columnWidth?: string })
                        .columnWidth,
                    } as never);
                    return (
                      <TableCell key={cellKey} {...cellRest}>
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </InfiniteScrollContainer>
      {Boolean(rows.length) && loading && <LoadingData />}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 40px;
`;
